import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Button,
  Grid,
  Divider,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Collapse,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Tooltip,
  LinearProgress,
  Tabs,
  Tab,
  Badge
} from '@mui/material';
import {
  ShoppingBag,
  LocalShipping,
  CheckCircle,
  Cancel,
  Payment,
  ExpandMore,
  ExpandLess,
  Visibility,
  Receipt,
  Refresh,
  Image as ImageIcon,
  LocationOn,
  Phone,
  Email,
  Replay
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { orderService } from '../services/api';
import { toast } from 'react-toastify';
import { format, subDays, subYears, isAfter, parseISO } from 'date-fns';
import { Link } from 'react-router-dom';
import COLORS from '../theme/colors';

const PAGE_SIZE = 5;
const DATE_FILTERS = [
  { label: 'Last 7 days', value: '7d' },
  { label: 'Last 30 days', value: '30d' },
  { label: 'Last 1 year', value: '1y' },
  { label: 'All', value: 'all' },
];

const Orders = () => {
  const { currentUser } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0); // 0: Ongoing, 1: Delivered, 2: Cancelled
  const [dateFilter, setDateFilter] = useState('7d');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);

  useEffect(() => {
    if (currentUser) {
      loadOrders();
    }
  }, [currentUser]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const response = await orderService.getOrders();
      console.log('Orders response:', response.data);
      
      // Ensure orders is an array and filter out any invalid orders
      const ordersData = response.data.orders || response.data || [];
      const validOrders = Array.isArray(ordersData) ? ordersData.filter(order => order && order.id) : [];
      
      console.log('Valid orders:', validOrders);
      setOrders(validOrders);
    } catch (error) {
      console.error('Error loading orders:', error);
      toast.error('Failed to load orders');
      setOrders([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending': return 'warning';
      case 'confirmed': return 'info';
      case 'processing': return 'info';
      case 'shipped': return 'primary';
      case 'delivered': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid': return 'success';
      case 'pending': return 'warning';
      case 'failed': return 'error';
      case 'awaiting_payment': return 'warning';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending': return <ShoppingBag />;
      case 'confirmed': return <CheckCircle />;
      case 'processing': return <Refresh />;
      case 'shipped': return <LocalShipping />;
      case 'delivered': return <CheckCircle />;
      case 'cancelled': return <Cancel />;
      default: return <ShoppingBag />;
    }
  };

  const formatDate = (value) => {
    if (!value) return 'N/A';
    try {
      let date;
      // Firestore Timestamp object
      if (value?.toDate && typeof value.toDate === 'function') {
        date = value.toDate();
      } else if (value && typeof value === 'object' && 'seconds' in value && typeof value.seconds === 'number') {
        date = new Date(value.seconds * 1000);
      } else if (typeof value === 'number') {
        // Distinguish seconds vs millis (assume > 10^12 is ms)
        date = value < 1e12 ? new Date(value * 1000) : new Date(value);
      } else if (typeof value === 'string') {
        // Try parsing ISO or fallback replacements
        // Replace Firestore console export style like 'August 26, 2025 at 11:10:01 AM UTC+5:30'
        const firestoreHumanPattern = /at\s/; // crude check
        if (firestoreHumanPattern.test(value) && !/T\d{2}:\d{2}/.test(value)) {
          // Attempt to convert to ISO: Use Date.parse after simplifying
            // Remove ' at' and timezone label modifications
            // Example: 'August 26, 2025 at 11:10:01 AM UTC+5:30'
          const cleaned = value
            .replace(' at', '')
            .replace('UTC', 'GMT'); // GMT offset parsing supported
          date = new Date(cleaned);
        } else {
          date = new Date(value);
        }
      } else if (value instanceof Date) {
        date = value;
      }
      if (!date || isNaN(date.getTime())) return 'Invalid Date';
      return format(date, 'PPp');
    } catch (e) {
      console.error('Failed to format date:', value, e);
      return 'Invalid Date';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };


  const canCancelOrder = (order) => {
    if (!order || !order.status) return false;
    const status = order.status.toLowerCase();
    // Allow cancellation only for pending and confirmed orders, not for inProduction or later stages
    return status === 'pending' || status === 'confirmed';
  };

  const canRetryPayment = (order) => {
    if (!order || !order.paymentStatus) return false;
    const paymentStatus = order.paymentStatus.toLowerCase();
    const orderStatus = (order.status || 'pending').toLowerCase();
    return paymentStatus === 'awaiting_payment' && orderStatus === 'pending';
  };

  const handleCancelOrder = async (orderId) => {
    if (window.confirm('Are you sure you want to cancel this order?')) {
      try {
        await orderService.cancelOrder(orderId);
        toast.success('Order cancelled successfully');
        loadOrders();
      } catch (error) {
        console.error('Error cancelling order:', error);
        toast.error('Failed to cancel order');
      }
    }
  };

  const handleRetryPayment = async (order) => {
    try {
      console.log('=== Retrying Payment ===');
      console.log('Order:', order);
      
      const response = await orderService.retryPayment(order.id);
      console.log('Retry payment response:', response.data);
      
      if (response.data.success && response.data.paymentData) {
        const paymentData = response.data.paymentData;
        console.log('PayU Payment Data:', JSON.stringify(paymentData, null, 2));
        
        // Create a form and submit to PayU
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = paymentData.paymentUrl; // Use paymentUrl instead of action
        form.target = '_self';
        
        // Add all payment parameters as hidden inputs
        Object.keys(paymentData.params).forEach(key => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = key;
          input.value = paymentData.params[key];
          form.appendChild(input);
          console.log(`Form field: ${key} = ${paymentData.params[key]}`);
        });
        
        console.log('Submitting form to:', paymentData.paymentUrl);
        document.body.appendChild(form);
        form.submit();
        document.body.removeChild(form);
        
        toast.info('Redirecting to payment gateway...');
      } else {
        toast.error('Failed to initiate payment retry');
      }
    } catch (error) {
      console.error('Error retrying payment:', error);
      toast.error('Failed to retry payment');
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setPage(0);
  };

  // Filter orders based on active tab
  const getFilteredOrders = () => {
    switch (activeTab) {
      case 0: // Ongoing
        return orders.filter(order => {
          const status = (order.status || 'pending').toLowerCase();
          return ['pending', 'confirmed', 'processing', 'shipped'].includes(status);
        });
      case 1: // Delivered
        return orders.filter(order => {
          const status = (order.status || 'pending').toLowerCase();
          return status === 'delivered';
        });
      case 2: // Cancelled
        return orders.filter(order => {
          const status = (order.status || 'pending').toLowerCase();
          return status === 'cancelled';
        });
      default:
        return orders;
    }
  };

  const getOrderCounts = () => {
    const ongoing = orders.filter(order => {
      const status = (order.status || 'pending').toLowerCase();
      return ['pending', 'confirmed', 'processing', 'shipped'].includes(status);
    }).length;

    const delivered = orders.filter(order => {
      const status = (order.status || 'pending').toLowerCase();
      return status === 'delivered';
    }).length;

    const cancelled = orders.filter(order => {
      const status = (order.status || 'pending').toLowerCase();
      return status === 'cancelled';
    }).length;

    return { ongoing, delivered, cancelled };
  };


  // --- Filtering, searching, and pagination ---
  const filteredOrders = useMemo(() => {
    let filtered = orders;
    // Date filter
    if (dateFilter !== 'all') {
      const now = new Date();
      let cutoff;
      if (dateFilter === '7d') cutoff = subDays(now, 7);
      else if (dateFilter === '30d') cutoff = subDays(now, 30);
      else if (dateFilter === '1y') cutoff = subYears(now, 1);
      filtered = filtered.filter(order => {
        let dt = order.createdAt;
        if (typeof dt === 'string') dt = parseISO(dt);
        else if (dt?.toDate) dt = dt.toDate();
        else if (dt?.seconds) dt = new Date(dt.seconds * 1000);
        else if (typeof dt === 'number') dt = new Date(dt < 1e12 ? dt * 1000 : dt);
        else if (!(dt instanceof Date)) dt = new Date(dt);
        return isAfter(dt, cutoff);
      });
    }
    // Tab filter
    filtered = filtered.filter(order => {
      const status = (order.status || 'pending').toLowerCase();
      if (activeTab === 0) return ['pending', 'confirmed', 'processing', 'shipped'].includes(status);
      if (activeTab === 1) return status === 'delivered';
      if (activeTab === 2) return status === 'cancelled';
      return true;
    });
    // Search filter
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      filtered = filtered.filter(order => {
        const fields = [
          order.orderNumber,
          order.id,
          order.transactionId,
          order.userName,
          order.userEmail,
          order.paymentResponse?.firstname,
          order.paymentResponse?.email,
          order.shippingAddress?.name,
          order.shippingAddress?.email,
          order.shippingAddress?.phone,
          (order.items || []).map(i => i.name).join(' ')
        ].filter(Boolean).join(' ').toLowerCase();
        return fields.includes(q);
      });
    }
    return filtered;
  }, [orders, dateFilter, activeTab, search]);

  const paginatedOrders = useMemo(() => {
    const start = page * PAGE_SIZE;
    return filteredOrders.slice(start, start + PAGE_SIZE);
  }, [filteredOrders, page]);

  const pageCount = Math.ceil(filteredOrders.length / PAGE_SIZE);

  // --- UI ---
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading your orders...
        </Typography>
      </Container>
    );
  }

  if (!currentUser) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="info">
          Please log in to view your orders.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} mb={3} gap={2}>
        <Typography variant="h5" fontWeight={600} color={COLORS.primary.main} gutterBottom>My Orders</Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={loadOrders}
          disabled={loading}
          sx={{ borderColor: COLORS.primary.main, color: COLORS.primary.main, '&:hover': { borderColor: COLORS.primary.dark, color: COLORS.primary.dark } }}
        >
          Refresh
        </Button>
      </Box>

      {/* Filter/Search Bar */}
      <Paper sx={{ mb: 3, p: 2, background: COLORS.background.light, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', justifyContent: 'space-between' }}>
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          {DATE_FILTERS.map(opt => (
            <Button
              key={opt.value}
              variant={dateFilter === opt.value ? 'contained' : 'outlined'}
              size="small"
              sx={{
                background: dateFilter === opt.value ? COLORS.primary.main : COLORS.background.paper,
                color: dateFilter === opt.value ? COLORS.primary.contrastText : COLORS.text.primary,
                borderColor: COLORS.primary.main,
                fontWeight: 500,
                minWidth: 110
              }}
              onClick={() => { setDateFilter(opt.value); setPage(0); }}
            >
              {opt.label}
            </Button>
          ))}
        </Box>
        <Box>
          <input
            type="text"
            placeholder="Search orders..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(0); }}
            style={{
              padding: '8px 12px',
              borderRadius: 6,
              border: `1px solid ${COLORS.border.medium}`,
              outline: 'none',
              fontSize: 15,
              minWidth: 180
            }}
          />
        </Box>
      </Paper>

      {/* Order Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          centered
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Ongoing" />
          <Tab label="Delivered" />
          <Tab label="Cancelled" />
        </Tabs>
      </Paper>

      {orders.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', background: COLORS.background.light }}>
          <ShoppingBag sx={{ fontSize: 80, color: COLORS.text.disabled, mb: 2 }} />
          <Typography variant="h6" gutterBottom>No orders found</Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            You haven't placed any orders yet. Start shopping to see your orders here!
          </Typography>
          <Button variant="contained" href="/" sx={{ background: COLORS.primary.main, color: COLORS.primary.contrastText }}>
            Start Shopping
          </Button>
        </Paper>
      ) : filteredOrders.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', background: COLORS.background.light }}>
          <Typography variant="h6" gutterBottom>No orders match your filters</Typography>
          <Typography variant="body2" color="text.secondary">Try changing your filters or search.</Typography>
        </Paper>
      ) : (
        <Box>
          {paginatedOrders.map(order => (
            <Paper key={order.id} sx={{ mb: 3, p: 3, borderRadius: 3, background: COLORS.background.paper, border: `1px solid ${COLORS.border.light}` }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600} color={COLORS.secondary.main}>
                    Recent Order #{order.orderNumber || order.id || 'Unknown'}
                  </Typography>
                  <Typography variant="body2" color={COLORS.text.secondary} mb={1}>
                    Placed {formatDate(order.createdAt)} • {order.items?.length || 0} items • {formatCurrency(order.pricing?.total || order.total || 0)}
                  </Typography>
                </Box>
                <Box display="flex" gap={1}>
                  <Button
                    variant="outlined"
                    size="small"
                    component={Link}
                    to={`/orders/${order.id}`}
                    sx={{ borderColor: COLORS.primary.main, color: COLORS.primary.main, fontWeight: 500, '&:hover': { borderColor: COLORS.primary.dark, color: COLORS.primary.dark } }}
                  >
                    View Details
                  </Button>
                  {/*<Button
                    variant="contained"
                    size="small"
                    sx={{ background: COLORS.primary.main, color: COLORS.primary.contrastText, fontWeight: 500, '&:hover': { background: COLORS.primary.dark } }}
                  >
                    Download Invoice
                  </Button>*/}
                </Box>
              </Box>
              <LinearProgress
                variant="determinate"
                value={getProgressValue(order.status || 'pending')}
                sx={{ height: 8, borderRadius: 4, background: COLORS.background.light, '& .MuiLinearProgress-bar': { background: COLORS.primary.main } }}
              />
            </Paper>
          ))}
          {/* Pagination Controls */}
          {pageCount > 1 && (
            <Box display="flex" justifyContent="center" alignItems="center" gap={1} mt={2}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                sx={{ minWidth: 36, borderColor: COLORS.primary.main, color: COLORS.primary.main }}
              >
                Prev
              </Button>
              <Typography variant="body2" color={COLORS.text.secondary}>
                Page {page + 1} of {pageCount}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setPage(p => Math.min(pageCount - 1, p + 1))}
                disabled={page === pageCount - 1}
                sx={{ minWidth: 36, borderColor: COLORS.primary.main, color: COLORS.primary.main }}
              >
                Next
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Container>
  );
};

// Helper function to get progress value based on status
const getProgressValue = (status) => {
  switch (status?.toLowerCase()) {
    case 'pending': return 20;
    case 'confirmed': return 40;
    case 'processing': return 60;
    case 'shipped': return 80;
    case 'delivered': return 100;
    case 'cancelled': return 0;
    default: return 0;
  }
};

export default Orders;
